import {
  keyAttr,
  keyMapItem,
  LayerKeysMap,
  DualModeKey,
  fnKeyType,
  DKS,
  RS,
  SOCD,
  Macro,
  macro,
  mouseKey,
} from '../../../../types/keyboardKeys'
import { KeyboardKeysFeature } from '../../../../features/keyboard-keys/contract'
import { ADDRESS_TELINK } from '../../protocols/address'
import { sendRead, sendWrite } from '../../capabilities/memory'
import { getAddress } from '../../../../utils/common'
import {
  DeepNestedLevelAddress,
  CommonAddress,
  LevelAddress,
  NestedLevelAddress,
} from '../../../../types/protocol'
import { isMacOS } from '../../../../utils/envString'
import {
  genrateMacroPackageAddressLevel,
  indexToRowCol,
  isValidRowCol,
  rowColToIndex,
} from '../../protocols/AddressGenerators'
import { platform } from '../../../../types/config'
import { MemoryMacroProfile, MemoryMacroStore } from '../../../shared/feature-impls/memory-macro'

const maxPackageLen = 24 // 单包最大有效数据长度
// Telink A4 指令只支持 8 的倍数读取长度，packageReadLength 需对齐到 8 的倍数
const TELINK_MACRO_PACKAGE_READ_LENGTH = 24
const TELINK_MEMORY_MACRO_PROFILE: MemoryMacroProfile = {
  packageAddresses: ADDRESS_TELINK.MACRO_PACKAGE_ADDRESS,
  nameAddresses: ADDRESS_TELINK.MACRO_NAME_ADDRESS,
  maxPackageLen,
  packageReadLength: TELINK_MACRO_PACKAGE_READ_LENGTH,
  listBatchSize: 8,
  activationDelayMs: 200,
  allowEmptyMacroPayload: true,
  generatePackageAddresses: genrateMacroPackageAddressLevel,
  isEmptyListName: (bytes) => bytes.every((value) => value === 255),
}

/**
 * @file TelinkKeys.ts
 * @author Rapoo Device SDK Team
 * @date 2026-07-23
 * @description Telink 键位功能实现（与 CH585M 地址池一致）
 */
export class TelinkKeyboardKeys extends KeyboardKeysFeature {
  private readonly macroStore = new MemoryMacroStore<Macro>({
    device: this.device,
    profile: TELINK_MEMORY_MACRO_PROFILE,
    read: sendRead,
    write: sendWrite,
  })

  private targetAdderss(isFN?: boolean, platform?: platform): DeepNestedLevelAddress {
    const isTargetMac = platform === 'mac' || (!platform && isMacOS)

    return isTargetMac
      ? isFN
        ? ADDRESS_TELINK.MAC_FN_KEY_MATRIX
        : ADDRESS_TELINK.MAC_KEY_MATRIX
      : isFN
        ? ADDRESS_TELINK.WIN_FN_KEY_MATRIX
        : ADDRESS_TELINK.WIN_KEY_MATRIX
  }

  getLayerKeyCount(isFN?: boolean, platform?: platform): number {
    const addressMap = this.targetAdderss(isFN, platform)[
      this.device.currentConfig.index
    ] as NestedLevelAddress
    let count = 0
    for (const row in addressMap) {
      const colMap = addressMap[row] as LevelAddress
      count += Object.keys(colMap).length
    }
    return count
  }

  async getLayerKeysMap(isFN?: boolean, platform?: platform): Promise<LayerKeysMap> {
    const ADDRESSMap = this.targetAdderss(isFN, platform)[this.device.currentConfig.index]
    const readPromises = [] as Promise<keyMapItem>[]
    const LayoutKeys = this.device.manifest.layout.keys
    for (const row in ADDRESSMap) {
      const colADDRESS = ADDRESSMap[row]
      for (const col in colADDRESS) {
        if (!isValidRowCol(parseInt(row), parseInt(col), LayoutKeys)) {
          continue
        }
        const ADDRESS = colADDRESS[col]
        const readPromise = new Promise<keyMapItem>((resolve) => {
          sendRead(this.device, ADDRESS, 0x10, (res: DataView) => {
            const keyAttr = this.disposeRead(res)
            const index = rowColToIndex(parseInt(row), parseInt(col))
            const keyMapItem: keyMapItem = {
              index,
              keyAttr,
            }
            resolve(keyMapItem)
          })
        })
        readPromises.push(readPromise)
      }
    }
    return await Promise.all(readPromises)
  }

  async *getLayerKeysMapStream(
    isFN?: boolean,
    batchSize: number = 22,
    platform?: platform,
  ): AsyncIterable<LayerKeysMap> {
    const addressMap = this.targetAdderss(isFN, platform)[
      this.device.currentConfig.index
    ] as NestedLevelAddress
    const keyAddressList: { row: number; col: number; address: CommonAddress }[] = []

    const LayoutKeys = this.device.manifest.layout.keys

    for (const rowStr in addressMap) {
      const row = parseInt(rowStr, 10)
      const colAddressMap = addressMap[row] as LevelAddress

      for (const colStr in colAddressMap) {
        const col = parseInt(colStr, 10)
        const address = colAddressMap[col] as CommonAddress

        if (!isValidRowCol(row, col, LayoutKeys)) {
          continue
        }

        keyAddressList.push({ row, col, address })
      }
    }

    const totalCount = keyAddressList.length
    for (let start = 0; start < totalCount; start += batchSize) {
      const end = Math.min(start + batchSize, totalCount)
      const batchKeys = keyAddressList.slice(start, end)

      const batchPromises = batchKeys.map(({ row, col, address }) => {
        return new Promise<keyMapItem>((resolve) => {
          sendRead(this.device, address, 0x10, (res: DataView) => {
            const keyAttr = this.disposeRead(res)
            const index = rowColToIndex(row, col)
            resolve({ index, keyAttr })
          })
        })
      })

      const batchResult = await Promise.all(batchPromises)
      yield batchResult

      await new Promise((resolve) => setTimeout(resolve, 0))
    }
  }

  // ==================== 解析/序列化 ====================

  private disposeRead(result: DataView): keyAttr {
    const data = {} as keyAttr
    data.type = result.getUint8(4)
    switch (result.getUint8(4)) {
      case 0x00:
        break
      case 0x01:
        const length = result.getUint8(5)
        data.modifyKey = result.getUint8(6)
        data.code = []
        for (let i = 0; i < Math.min(length, 6); i++) {
          data.code.push(result.getUint8(7 + i))
        }
        break
      case 0x02:
        data.code = [result.getUint16(5, true)]
        break
      case 0x03:
        data.code = [result.getUint8(5)]
        break
      case 0x04:
        const mouseBtnByte = result.getUint8(5)
        const mouseKey: mouseKey = [
          (mouseBtnByte >> 0) & 0x01,
          (mouseBtnByte >> 1) & 0x01,
          (mouseBtnByte >> 2) & 0x01,
          (mouseBtnByte >> 3) & 0x01,
          (mouseBtnByte >> 4) & 0x01,
        ]
        const axisHighByte = result.getUint8(6)
        const xHigh4 = (axisHighByte & 0xf0) >> 4
        const yHigh4 = axisHighByte & 0x0f
        const xLow8 = result.getUint8(7)
        const yLow8 = result.getUint8(8)
        let x = (xHigh4 << 8) | xLow8
        let y = (yHigh4 << 8) | yLow8
        if (x & 0x800) x -= 0x1000
        if (y & 0x800) y -= 0x1000
        const verticalWheel = result.getInt8(9)
        const horizontalWheel = result.getInt8(10)
        data.mouse = {
          mouseKey,
          x,
          y,
          colWheelNum: verticalWheel,
          rowWheelNum: horizontalWheel,
        }
        break
      case 0x05:
        const mouseKeyByte = result.getUint8(5)
        const parsedMouseKey: mouseKey = [
          (mouseKeyByte >> 0) & 0x01,
          (mouseKeyByte >> 1) & 0x01,
          (mouseKeyByte >> 2) & 0x01,
          (mouseKeyByte >> 3) & 0x01,
          (mouseKeyByte >> 4) & 0x01,
        ]
        const clickNum = result.getUint8(6)
        const pressTime = result.getUint16(7, true)
        const relaseTime = result.getUint16(9, true)
        data.mouseKey = {
          mouseKey: parsedMouseKey,
          clickNum,
          pressTime,
          relaseTime,
        }
        break
      case 0x06:
      case 0x07:
        const wheelType = result.getUint8(5)
        const wheelNumber = result.getInt8(6)
        const wheelTime = result.getUint16(7, true)
        const wheelData = { type: wheelType, number: wheelNumber, time: wheelTime }
        if (data.type === 0x06) {
          data.colWheel = wheelData
        } else {
          data.rowWheel = wheelData
        }
        break
      case 0x08:
        const macro: macro = {
          loop: result.getUint16(5, true),
          index: result.getUint8(7),
        }
        data.macro = macro
        break
      case 0x09:
        data.code = [result.getUint8(5)]
        data.confIndex = result.getUint8(6)
        break
      case 0x0a:
      case 0x0b:
        const dualModeKey: DualModeKey = {
          time: result.getUint16(5, true),
          clickType: result.getUint8(7) as fnKeyType,
          clickKeyCode: result.getUint16(8, true),
          longPressType: result.getUint8(10) as fnKeyType,
          longPressKeyCode: result.getUint16(11, true),
        }
        data.dualModeKey = dualModeKey
        break
      case 0x0c:
        const dks: DKS = {
          actPoint1: 0,
          actPoint2: 0,
          releasePoint1: 0,
          releasePoint2: 0,
          triggerBits: [],
          keyCodes: [],
        }
        const B1 = result.getUint8(5)
        const B2 = result.getUint8(6)
        const B3 = result.getUint8(7)
        const B4 = result.getUint8(8)
        const B5 = result.getUint8(9)
        const B6 = result.getUint8(10)
        dks.actPoint1 = B1 + ((B2 & 0x0f) << 8)
        dks.actPoint2 = ((B2 & 0xf0) >> 4) + (B3 << 4)
        dks.releasePoint1 = B4 + ((B5 & 0x0f) << 8)
        dks.releasePoint2 = ((B5 & 0xf0) >> 4) + (B6 << 4)
        dks.triggerBits = [
          result.getUint8(11),
          result.getUint8(12),
          result.getUint8(13),
          result.getUint8(14),
        ]
        dks.keyCodes = [
          result.getUint8(15),
          result.getUint8(16),
          result.getUint8(17),
          result.getUint8(18),
        ]
        data.dks = dks
        break
      case 0x0d:
        const rs = {} as RS
        rs.combineRT = result.getUint8(5)
        rs.relatedKeyRow = result.getUint8(6)
        rs.relatedKeyCol = result.getUint8(7)
        rs.relatedKeyIndex = rowColToIndex(rs.relatedKeyRow, rs.relatedKeyCol)
        rs.triggerKeyType = result.getUint8(8) as fnKeyType
        rs.triggerKeyCode = result.getUint8(9)
        rs.triggerKeyExtCode = result.getUint8(10)
        rs.triggerThreshold = result.getUint16(11, true)
        data.rs = rs
        break
      case 0x0e:
        const socd = {} as SOCD
        socd.subMode = result.getUint8(5)
        socd.combineRT = result.getUint8(6)
        socd.triggerWhenBottom = result.getUint8(7)
        socd.relatedKeyRow = result.getUint8(8)
        socd.relatedKeyCol = result.getUint8(9)
        socd.relatedKeyIndex = rowColToIndex(socd.relatedKeyRow, socd.relatedKeyCol)
        socd.triggerKeyType = result.getUint8(10) as fnKeyType
        socd.triggerKeyCode = result.getUint8(11)
        socd.triggerKeyExtCode = result.getUint8(12)
        socd.selfPriority = result.getUint8(13)
        socd.triggerThreshold = result.getUint16(14, true)
        data.socd = socd
        break
      case 0x0f:
        break
    }
    return data
  }

  async setLayerKeysMap(keysMap: keyMapItem, isFN?: boolean, platform?: platform): Promise<void> {
    const { index } = keysMap
    const { row, col } = indexToRowCol(index)
    const ADDRESS = getAddress(
      this.targetAdderss(isFN, platform),
      this.device.currentConfig.index,
      row,
      col,
    )
    const data = this.disposeSend(keysMap.keyAttr)
    return sendWrite(this.device, ADDRESS, Array.from(data))
  }

  private disposeSend(data: keyAttr): Uint8Array {
    const buffer = new ArrayBuffer(16)
    const dv = new DataView(buffer)

    const type = data.type ?? 0x00
    dv.setUint8(0, type)

    switch (type) {
      case 0x00:
        break
      case 0x01:
        if (data.code?.length) {
          const length = Math.min(data.code.length, 6)
          dv.setUint8(1, length)
          dv.setUint8(2, data.modifyKey ?? 0x00)
          for (let i = 0; i < length; i++) {
            dv.setUint8(3 + i, data.code[i])
          }
        }
        break
      case 0x02:
        if (data.code?.length) {
          dv.setUint16(1, data.code[0], true)
        }
        break
      case 0x03:
        if (data.code?.length) {
          dv.setUint8(1, data.code[0])
        }
        break
      case 0x04:
        const mouse = data.mouse
        if (mouse) {
          let mouseBtnByte = 0x00
          const mouseKey = mouse.mouseKey ?? [0, 0, 0, 0, 0]
          if (mouseKey[0]) mouseBtnByte |= 0x01
          if (mouseKey[1]) mouseBtnByte |= 0x02
          if (mouseKey[2]) mouseBtnByte |= 0x04
          if (mouseKey[3]) mouseBtnByte |= 0x08
          if (mouseKey[4]) mouseBtnByte |= 0x10
          dv.setUint8(1, mouseBtnByte)

          let x = Math.max(Math.min(mouse.x ?? 0, 2047), -2048)
          let y = Math.max(Math.min(mouse.y ?? 0, 2047), -2048)
          if (x < 0) x += 0x1000
          if (y < 0) y += 0x1000

          const xHigh4 = (x >> 8) & 0x0f
          const xLow8 = x & 0xff
          const yHigh4 = (y >> 8) & 0x0f
          const yLow8 = y & 0xff

          const axisHighByte = (xHigh4 << 4) | yHigh4
          dv.setUint8(2, axisHighByte)
          dv.setUint8(3, xLow8)
          dv.setUint8(4, yLow8)

          const colWheel = Math.max(Math.min(mouse.colWheelNum ?? 0, 127), -128)
          dv.setInt8(5, colWheel)
          const rowWheel = Math.max(Math.min(mouse.rowWheelNum ?? 0, 127), -128)
          dv.setInt8(6, rowWheel)
        }
        break
      case 0x05:
        const mouseKeyObj = data.mouseKey
        if (mouseKeyObj) {
          let mouseBtnByte = 0x00
          const mouseKey = mouseKeyObj.mouseKey ?? [0, 0, 0, 0, 0]
          if (mouseKey[0]) mouseBtnByte |= 0x01
          if (mouseKey[1]) mouseBtnByte |= 0x02
          if (mouseKey[2]) mouseBtnByte |= 0x04
          if (mouseKey[3]) mouseBtnByte |= 0x08
          if (mouseKey[4]) mouseBtnByte |= 0x10
          dv.setUint8(1, mouseBtnByte)
          const clickNum = Math.max(Math.min(mouseKeyObj.clickNum ?? 0, 255), 0)
          dv.setUint8(2, clickNum)
          const pressTime = Math.max(Math.min(mouseKeyObj.pressTime ?? 0, 65535), 0)
          dv.setUint16(3, pressTime, true)
          const relaseTime = Math.max(Math.min(mouseKeyObj.relaseTime ?? 0, 65535), 0)
          dv.setUint16(5, relaseTime, true)
        }
        break
      case 0x06:
      case 0x07:
        const wheelObj = data.type === 0x06 ? data.colWheel : data.rowWheel
        if (wheelObj) {
          const wheelType = Math.max(Math.min(wheelObj.type ?? 0, 2), 0)
          dv.setUint8(1, wheelType)
          const num = Math.max(Math.min(wheelObj.number ?? 0, 127), -128)
          dv.setInt8(2, num)
          const time = Math.max(Math.min(wheelObj.time ?? 0, 65535), 0)
          dv.setUint16(3, time, true)
        }
        break
      case 0x08:
        if (data?.macro) {
          dv.setUint16(1, data.macro.loop ?? 0x00, true)
          dv.setUint8(3, data.macro.index ?? 0x00)
        }
        break
      case 0x09:
        if (data.code?.length) {
          dv.setUint8(1, data.code[0])
        }
        dv.setUint8(2, data.confIndex ?? 0x00)
        break
      case 0x0a:
      case 0x0b:
        const dualModeKey: DualModeKey = data.dualModeKey as DualModeKey
        if (dualModeKey) {
          dv.setUint16(1, dualModeKey.time, true)
          dv.setUint8(3, dualModeKey.clickType)
          dv.setUint16(4, dualModeKey.clickKeyCode, true)
          dv.setUint8(6, dualModeKey.longPressType)
          dv.setUint16(7, dualModeKey.longPressKeyCode, true)
        }
        break
      case 0x0c:
        const dks: DKS = data.dks as DKS
        if (dks) {
          const B1 = dks.actPoint1 & 0xff
          const B2_low = (dks.actPoint1 >> 8) & 0x0f
          const B2_high = (dks.actPoint2 & 0x0f) << 4
          const B3 = (dks.actPoint2 >> 4) & 0xff
          const B4 = dks.releasePoint1 & 0xff
          const B5_low = (dks.releasePoint1 >> 8) & 0x0f
          const B5_high = (dks.releasePoint2 & 0x0f) << 4
          const B6 = (dks.releasePoint2 >> 4) & 0xff
          const B2 = B2_high | B2_low
          const B5 = B5_high | B5_low
          dv.setUint8(1, B1)
          dv.setUint8(2, B2)
          dv.setUint8(3, B3)
          dv.setUint8(4, B4)
          dv.setUint8(5, B5)
          dv.setUint8(6, B6)
          if (dks.triggerBits && dks.triggerBits.length >= 4) {
            dv.setUint8(7, dks.triggerBits[0])
            dv.setUint8(8, dks.triggerBits[1])
            dv.setUint8(9, dks.triggerBits[2])
            dv.setUint8(10, dks.triggerBits[3])
          }
          if (dks.keyCodes && dks.keyCodes.length >= 4) {
            dv.setUint8(11, dks.keyCodes[0])
            dv.setUint8(12, dks.keyCodes[1])
            dv.setUint8(13, dks.keyCodes[2])
            dv.setUint8(14, dks.keyCodes[3])
          }
        }
        break
      case 0x0d:
        const rs: RS = data.rs as RS
        if (rs) {
          rs.combineRT = rs.combineRT ?? 0
          rs.relatedKeyIndex = rs.relatedKeyIndex ?? 0
          rs.triggerKeyType = rs.triggerKeyType ?? 0
          rs.triggerKeyCode = rs.triggerKeyCode ?? 0
          rs.triggerKeyExtCode = rs.triggerKeyExtCode ?? 0
          rs.triggerThreshold = rs.triggerThreshold ?? 0
          const { row: rsRow, col: rsCol } = indexToRowCol(rs.relatedKeyIndex)
          rs.relatedKeyRow = rsRow
          rs.relatedKeyCol = rsCol
          dv.setUint8(1, rs.combineRT)
          dv.setUint8(2, rsRow)
          dv.setUint8(3, rsCol)
          dv.setUint8(4, rs.triggerKeyType)
          dv.setUint8(5, rs.triggerKeyCode)
          dv.setUint8(6, rs.triggerKeyExtCode)
          dv.setUint16(7, rs.triggerThreshold, true)
        }
        break
      case 0x0e:
        const socd: SOCD = data.socd as SOCD
        if (socd) {
          socd.subMode = socd.subMode ?? 0
          socd.combineRT = socd.combineRT ?? 0
          socd.triggerWhenBottom = socd.triggerWhenBottom ?? 0
          socd.relatedKeyIndex = socd.relatedKeyIndex ?? 0
          socd.triggerKeyType = socd.triggerKeyType ?? 0
          socd.triggerKeyCode = socd.triggerKeyCode ?? 0
          socd.triggerKeyExtCode = socd.triggerKeyExtCode ?? 0
          socd.selfPriority = socd.selfPriority ?? 0
          socd.triggerThreshold = socd.triggerThreshold ?? 0
          const { row: socdRow, col: socdCol } = indexToRowCol(socd.relatedKeyIndex)
          socd.relatedKeyRow = socdRow
          socd.relatedKeyCol = socdCol
          dv.setUint8(1, socd.subMode)
          dv.setUint8(2, socd.combineRT)
          dv.setUint8(3, socd.triggerWhenBottom)
          dv.setUint8(4, socdRow)
          dv.setUint8(5, socdCol)
          dv.setUint8(6, socd.triggerKeyType)
          dv.setUint8(7, socd.triggerKeyCode)
          dv.setUint8(8, socd.triggerKeyExtCode)
          dv.setUint8(9, socd.selfPriority)
          dv.setUint16(10, socd.triggerThreshold, true)
        }
        break
      case 0x0f:
        break
    }

    return new Uint8Array(buffer)
  }

  async getMacroList(): Promise<Macro[]> {
    return this.macroStore.getMacroList()
  }

  async *getMacroListStream(batchSize: number = 8): AsyncIterable<Macro[]> {
    for await (const macros of this.macroStore.getMacroListStream(batchSize)) {
      yield macros
    }
  }

  async getMacroByIndex(index: number): Promise<Macro> {
    return this.macroStore.getMacroByIndex(index)
  }

  async setMacroByIndex(macro: Macro, index: number): Promise<void> {
    return this.macroStore.setMacroByIndex(macro, index)
  }
}
